import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { InsertRiskAssessmentResultConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Maps tRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
  Version: number;
  IsLatest: boolean;
}): InsertRiskAssessmentResultConfigMutation => ({
  insertRiskAssessmentResultConfigApi: {
    __typename: 'InsertRiskAssessmentResultConfigApiOutput',
    Id: trpcData.Id,
    Version: trpcData.Version,
    IsLatest: trpcData.IsLatest,
  },
});

export const useInsertRiskAssessmentResultConfigTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResultConfig.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getLatest.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getByIds.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    mutate: async (variables: {
      variables: { Config: RiskAssessmentResultConfig; Type: string };
    }): Promise<InsertRiskAssessmentResultConfigMutation> => {
      const result = await mutation.mutateAsync({
        Config: variables.variables.Config,
        Type: variables.variables.Type as
          | 'risk_assessment'
          | 'compliance_monitoring'
          | 'internal_audit',
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
